import React, { useState, useEffect, useCallback } from 'react';
import { getTradePositionHistory, getPostTradeJournalHistory, getToolTip } from "./function";
import { Tooltip } from 'antd';
import { TOOLTIP_CONFIG, getTooltipContent } from './tooltipConfig';
import { RotateCcw, Save, Info as InfoIcon } from "lucide-react";
import { t } from 'i18next';
import { API_URL } from "env";
import {
  PositionTimeLine,
  PositionTimeLineMoveSL,
  PositionTimeLineClose,
  DayDetailTracker,
  PostTradeData,
  FormConfigs,
  Journal,
  PostTradeTooltipData
} from "./type";

import './StepPostTrade_v2.scss';
import { getEmotionsFromFormConfig } from './utility_function';
import { matchesPair } from './pairs';

// Bảng màu hỗ trợ giao diện
const C = {
  border: 'oklch(0.92 0.005 90)',
  neutralBd: 'oklch(0.88 0.005 90)',
  surface: '#ffffff',
  muted: 'oklch(0.45 0.01 90)',
  faint: 'oklch(0.65 0.01 90)',
  green: 'oklch(0.55 0.13 145)',
  red: 'oklch(0.55 0.15 25)',
  blue: 'oklch(0.55 0.15 240)',
  white: '#ffffff',
};

export interface JournalTrade {
  id: string; // Tương ứng với position_id
  symbol: string;
  side: 'BUY' | 'SELL';
  pnl: number;
  result: 'win' | 'loss' | 'break_even';
  setupScore: number;
  riskScore: number;
  psyScore: number;
  totalScore: number;
  dirPlanned: string;
  dirActual: string;
  slPlanned: string;
  slActual: string;
  tpPlanned: string;
  tpActual: string;
  moodMode?: 'quick' | 'full';
  emotions?: string[]; // Lưu danh sách các emotion value
  timeline?: PositionTimeLine[];
  postTradeData: PostTradeData;
}

const getScoreColor = (score: number, max: number) => {
  const pct = max > 0 ? score / max : 0;
  if (pct >= 0.85) return "oklch(0.45 0.13 145)";
  if (pct >= 0.5) return "oklch(0.55 0.1 75)";
  return "oklch(0.5 0.16 25)";
};

const getPillStyle = (score: number, max: number): React.CSSProperties => {
  const pct = max > 0 ? score / max : 0;
  const bg = pct >= 0.85 ? "oklch(0.94 0.06 145)" : pct >= 0.5 ? "oklch(0.95 0.05 75)" : "oklch(0.94 0.03 30)";
  const fg = getScoreColor(score, max);
  return {
    display: 'inline-flex',
    padding: '3px 9px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: 700,
    background: bg,
    color: fg
  };
};

function isCloseEvent(data: PositionTimeLineMoveSL | PositionTimeLineClose): data is PositionTimeLineClose {
  return (data as PositionTimeLineClose).close_price !== undefined;
}

// Hàm lấy ngày hôm nay định dạng YYYY-MM-DD
const getTodayString = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Hàm bóc tách PnL từ chuỗi pnl_text
const parsePnlFromText = (pnlText?: string): number => {
  if (!pnlText) return 0;
  const cleaned = pnlText.replace(/[^0-9.-]/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

const panelStyle: React.CSSProperties = {
  background: 'oklch(99% 0.002 80)',
  border: '1px solid oklch(91% 0.01 80)',
  borderRadius: '14px',
  padding: '16px'
};

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface StepPostTradeV2Props {
  account_number?: string;
  date?: string;
  symbol?: string;
  journal_data?: Journal | null;
  form_configs?: FormConfigs | null;
  can_edit?: boolean;
  onReload?: () => void;
}

const StepPostTradeV2: React.FC<StepPostTradeV2Props> = ({
  account_number = "",
  date = "",
  symbol = "",
  journal_data = null,
  form_configs = null,
  can_edit = false,
  onReload
}) => {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "win" | "loss">("all");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [trades, setTrades] = useState<JournalTrade[]>([]);
  const [tooltips, setTooltips] = useState<PostTradeTooltipData | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch API tooltips một lần khi mount
  useEffect(() => {
    getToolTip().then((res) => {
      if (res?.success && res.data) {
        setTooltips(res.data);
      }
    }).catch(err => console.error("Lỗi khi tải Tooltips:", err));
  }, []);

  // Trạng thái Save theo từng Trade
  const [saveStatusMap, setSaveStatusMap] = useState<Record<string, SaveStatus>>({});

  // 1. Lấy dữ liệu cảm xúc động từ prop form_configs
  const emotionGroups = React.useMemo(() => {
    const configsToUse = form_configs?.post_trade || form_configs?.in_trade || (Array.isArray(form_configs) ? form_configs : []);
    return getEmotionsFromFormConfig(configsToUse);
  }, [form_configs]);

  // 2. Lấy dữ liệu rút gọn (Quick view): Lấy 2 cảm xúc đầu tiên từ mỗi nhóm
  const quickEmotions = React.useMemo(() => {
    return emotionGroups.flatMap((group) => group.items.slice(0, 2));
  }, [emotionGroups]);

  // Lấy dữ liệu từ API PostTrade và map Timeline
  const fetchData = useCallback(async () => {
    if (!account_number) return;
    setLoading(true);

    try {
      const apiFilter: "WIN" | "LOSS" | undefined = filter === "win" ? "WIN" : filter === "loss" ? "LOSS" : undefined;
      const apiSearch: string | undefined = search.trim() !== "" ? search.trim() : undefined;
      const apiDate: string = date && date.trim() !== "" ? date : getTodayString();

      const [postTradeRes, positionTimelineRes] = await Promise.all([
        getPostTradeJournalHistory(account_number, apiSearch, apiFilter, apiDate),
        getTradePositionHistory(account_number, apiDate)
      ]);

      const timelineMap = new Map<string, PositionTimeLine[]>();
      if (Array.isArray(positionTimelineRes)) {
        positionTimelineRes.forEach((item: DayDetailTracker) => {
          timelineMap.set(String(item.tracker_id), item.timeline || []);
        });
      }

      if (postTradeRes?.success && Array.isArray(postTradeRes.data)) {
        const postTradeList: PostTradeData[] = postTradeRes.data;

        const formattedTrades: JournalTrade[] = postTradeList
          .filter((pt) => {
            return matchesPair(pt.symbol, symbol);
          })
          .map((pt) => {
            const posId = String(pt.position_id);
            const pnlText = pt.result_evaluation?.pnl_text || "";
            const pnlValue = parsePnlFromText(pnlText);

            const isWin = pnlValue > 0 || pnlText.includes("+") || pt.result_evaluation?.status === 'WIN';
            const isLoss = pnlValue < 0 || pnlText.includes("-") || pt.result_evaluation?.status === 'LOSS';
            const resultStatus: 'win' | 'loss' | 'break_even' = isWin ? 'win' : isLoss ? 'loss' : 'break_even';

            const tradeSide = (pt.side || 'BUY').toUpperCase();
            const isBuy = tradeSide === 'BUY';

            const execution = journal_data?.trade_executions?.find(e => String(e.position_id) === posId);
            const savedEmotions = execution?.post_trade_data?.post_emotion
              ? execution.post_trade_data.post_emotion.split(',').map(s => s.trim())
              : [];

            return {
              id: posId,
              symbol: pt.symbol || symbol,
              side: isBuy ? 'BUY' : 'SELL',
              pnl: pnlValue,
              result: resultStatus,
              setupScore: pt.header_scores?.setup || 0,
              riskScore: pt.header_scores?.risk || 0,
              psyScore: pt.header_scores?.psychology || 0,
              totalScore: pt.total_score || 0,
              dirPlanned: tradeSide,
              dirActual: tradeSide,
              slPlanned: "-",
              slActual: "-",
              tpPlanned: "-",
              tpActual: "-",
              moodMode: 'quick',
              emotions: savedEmotions, // Chứa mảng value cảm xúc
              timeline: timelineMap.get(posId) || [],
              postTradeData: pt
            };
          });

        setTrades(formattedTrades);
      } else {
        setTrades([]);
      }
    } catch (err) {
      console.error("Lỗi khi tải dữ liệu PostTradeV2:", err);
      setTrades([]);
    } finally {
      setLoading(false);
    }
  }, [account_number, date, symbol, search, filter, journal_data]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
    }, 1500);

    return () => clearTimeout(timer);
  }, [fetchData]);

  const toggleRow = (id: string) => {
    const next = new Set(expandedRows);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedRows(next);
  };

  const setMoodMode = (id: string, mode: 'quick' | 'full') => {
    setTrades(prev => prev.map(t => t.id === id ? { ...t, moodMode: mode } : t));
  };

  // Cập nhật: Thêm/Xóa dựa trên value của cảm xúc
  const toggleEmotion = (id: string, emotionValue: string) => {
    setTrades(prev => prev.map(t => {
      if (t.id !== id) return t;
      const currentEmotions = t.emotions || [];
      const hasEmotion = currentEmotions.includes(emotionValue);
      const updatedEmotions = hasEmotion
        ? currentEmotions.filter(e => e !== emotionValue)
        : [...currentEmotions, emotionValue];
      return { ...t, emotions: updatedEmotions };
    }));
  };

  const resetTrade = (tradeId: string) => {
    setTrades(prev => prev.map(t => {
      if (t.id !== tradeId) return t;
      return {
        ...t,
        moodMode: 'quick',
        emotions: []
      };
    }));
    setSaveStatusMap(prev => ({ ...prev, [tradeId]: 'idle' }));
  };

  const saveTrade = async (trade: JournalTrade) => {
    setSaveStatusMap(prev => ({ ...prev, [trade.id]: 'saving' }));

    try {
      // 1. Tìm bản ghi đã tồn tại trong journal_data dựa trên position_id và account_number
      const existingExecution = journal_data?.trade_executions?.find(
        (exec) =>
          String(exec.position_id) === String(trade.id) &&
          String(exec.account_number || account_number) === String(account_number)
      );

      // Lấy id nếu đã tồn tại
      const executionId = existingExecution?.id || null;

      // 2. Tạo payload kèm id
      const payload = {
        ...(executionId ? { id: executionId } : {}),
        journal_id: journal_data?.id || null,
        account_number: account_number || null,
        position_id: trade.id,
        symbol: trade.symbol,
        direction: trade.side,
        status: 'closed',
        result_status: trade.result === 'win' ? 'WIN' : trade.result === 'loss' ? 'LOSS' : 'BREAK_EVEN',
        result_pnl: trade.pnl,
        post_trade_data: {
          // Chuỗi phân cách bằng dấu phẩy các emotion value
          post_emotion: (trade.emotions || []).join(', '),
          best_thing: '',
          worst_thing: '',
          lesson_learned: '',
          mistake_to_avoid: ''
        },
        items: []
      };

      const res = await fetch(`${API_URL}/api/v1/be/checklist/trade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error(`Failed to save post-trade data: ${res.statusText}`);
      }

      setSaveStatusMap(prev => ({ ...prev, [trade.id]: 'saved' }));
      if (onReload) onReload();

      setTimeout(() => {
        setSaveStatusMap(prev => ({ ...prev, [trade.id]: 'idle' }));
      }, 2000);
    } catch (error) {
      console.error("Lỗi khi lưu lệnh:", error);
      setSaveStatusMap(prev => ({ ...prev, [trade.id]: 'error' }));

      setTimeout(() => {
        setSaveStatusMap(prev => ({ ...prev, [trade.id]: 'idle' }));
      }, 3000);
    }
  };

  const processedTrades = trades.map(r => {
    const pt = r.postTradeData;
    return {
      ...r,
      sideColor: r.side === "BUY" ? "oklch(0.55 0.13 145)" : "oklch(0.55 0.15 25)",
      resultLabel: pt.result_evaluation?.pnl_text
        ? `${r.result === 'win' ? '✓' : '✕'} ${pt.result_evaluation.pnl_text}`
        : (r.result === "win" ? `✓ Thắng +$${r.pnl}` : `✕ Thua -$${Math.abs(r.pnl)}`),
      resultColor: r.result === "win" ? "oklch(0.45 0.13 145)" : "oklch(0.5 0.16 25)",
      totalScoreColor: getScoreColor(r.totalScore, 100)
    };
  });

  const totalCount = trades.length;
  const winsCount = trades.filter(r => r.result === 'win').length;
  const lossesCount = trades.filter(r => r.result === 'loss').length;
  const avgScore = totalCount > 0
    ? Math.round(processedTrades.reduce((acc, cur) => acc + cur.totalScore, 0) / totalCount)
    : 0;

  return (
    <div className="step-posttrade-v2" style={{ margin: '0px auto', padding: '0 0px 64px' }}>
      <style>{`
        @keyframes jd-keyframes-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div className="pt-box" style={{ background: '#fff', border: '1px solid oklch(0.9 0.005 90)', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 1px 2px oklch(0.2 0 0 / 0.04)' }}>

        {/* Search & Filter */}
        <div className="pt-filter" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 20px', borderBottom: '1px solid oklch(0.93 0.005 90)' }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tìm theo mã, ticket ID..."
            style={{ flex: 1, minWidth: '180px', border: '1px solid oklch(0.88 0.005 90)', borderRadius: '9px', padding: '8px 12px', fontSize: '13.5px', outline: 'none', color: 'oklch(0.3 0.01 90)' }}
          />
          <div style={{ display: 'flex', gap: '6px' }}>
            {[
              { key: 'all', label: 'Tất cả' },
              { key: 'win', label: 'Thắng' },
              { key: 'loss', label: 'Thua' }
            ].map(f => (
              <div
                key={f.key}
                onClick={() => setFilter(f.key as any)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '8px',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  background: filter === f.key ? 'oklch(0.28 0.01 90)' : 'oklch(0.95 0.005 90)',
                  color: filter === f.key ? '#fff' : 'oklch(0.45 0.01 90)'
                }}
              >
                {f.label}
              </div>
            ))}
          </div>
          <div style={{ fontSize: '12.5px', color: 'oklch(0.55 0.01 90)', whiteSpace: 'nowrap' }}>
            {winsCount} thắng · {lossesCount} thua / {totalCount} lệnh
          </div>
        </div>

        {/* Header Bảng */}
        <div className="pt-head" style={{ display: 'grid', gridTemplateColumns: '1.5fr 0.85fr 0.85fr 0.85fr 0.9fr 0.7fr 32px', gap: '8px', padding: '10px 20px', fontSize: '11.5px', fontWeight: 700, letterSpacing: '0.03em', color: 'oklch(0.55 0.01 90)', textTransform: 'uppercase', background: 'oklch(0.98 0.003 90)', borderBottom: '1px solid oklch(0.93 0.005 90)', position: 'sticky', top: 0 }}>
          <div>Lệnh</div>
          <div>Setup /30</div>
          <div>Risk /30</div>
          <div>Tâm lý /40</div>
          <div>Kết quả</div>
          <div>Tổng</div>
          <div></div>
        </div>

        {/* List Lệnh */}
        <div>
          {loading && (
            <div style={{ padding: '30px', textAlign: 'center', fontSize: '13px', color: 'oklch(0.55 0.01 90)' }}>
              Đang tải dữ liệu đánh giá lệnh (PostTrade)...
            </div>
          )}

          {!loading && processedTrades.length === 0 && (
            <div style={{ padding: '30px', textAlign: 'center', fontSize: '13px', color: 'oklch(0.55 0.01 90)' }}>
              Không tìm thấy dữ liệu đánh giá lệnh nào.
            </div>
          )}

          {!loading && processedTrades.map((r) => {
            const isExpanded = expandedRows.has(r.id);
            const saveStatus = saveStatusMap[r.id] || 'idle';
            const pt = r.postTradeData;

            return (
              <div key={r.id} style={{ borderBottom: '1px solid oklch(0.94 0.005 90)' }}>
                <div
                  className="pt-row"
                  onClick={() => toggleRow(r.id)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.5fr 0.85fr 0.85fr 0.85fr 0.9fr 0.7fr 32px',
                    gap: '8px',
                    alignItems: 'center',
                    padding: '12px 20px',
                    cursor: 'pointer',
                    background: isExpanded ? 'oklch(0.97 0.01 75)' : 'transparent'
                  }}
                >
                  <div className="pt-row__info" style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                    <div style={{ width: '6px', height: '34px', borderRadius: '4px', background: r.sideColor, flex: 'none' }}></div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: 'oklch(0.28 0.01 90)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.symbol} <span style={{ fontWeight: 600, color: r.sideColor }}>{r.side}</span>
                      </div>
                      <div style={{ fontSize: '12px', color: 'oklch(0.55 0.01 90)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        #{r.id} · P&L {r.pnl >= 0 ? `+$${r.pnl}` : `-$${Math.abs(r.pnl)}`}
                      </div>
                    </div>
                  </div>

                  {/* data-label: shown as a caption on phones, where the table header row is hidden */}
                  <div className="pt-row__setup" data-label="Setup"><span style={getPillStyle(r.setupScore, 30)}>{r.setupScore}/30</span></div>
                  <div className="pt-row__risk" data-label="Risk"><span style={getPillStyle(r.riskScore, 30)}>{r.riskScore}/30</span></div>
                  <div className="pt-row__psy" data-label="Tâm lý"><span style={getPillStyle(r.psyScore, 40)}>{r.psyScore}/40</span></div>

                  <div className="pt-row__result" style={{ fontSize: '13.5px', fontWeight: 700, color: r.resultColor }}>{r.resultLabel}</div>
                  <div className="pt-row__total" data-label="Tổng" style={{ fontSize: '14px', fontWeight: 800, color: r.totalScoreColor }}>{r.totalScore}</div>
                  <div className="pt-row__arrow" style={{ display: 'flex', justifyContent: 'center', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s', color: 'oklch(0.55 0.01 90)' }}>
                    ▾
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="pt-expand" style={{ padding: '18px 20px 22px 46px', background: 'oklch(0.985 0.003 90)', display: 'flex', flexDirection: 'column', gap: '16px', fontSize: '13px' }}>

                    {/* Breakdown Scores */}
                    <div className="pt-breakdown" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
                      {/* Setup Block */}
                      <div style={{ border: '1px solid oklch(0.92 0.005 90)', borderRadius: '12px', padding: '12px 14px', background: '#fff' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '12px', fontWeight: '700', color: 'oklch(0.4 0.01 90)', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                              Setup
                            </span>
                            {getTooltipContent(tooltips, TOOLTIP_CONFIG.setupHeader) && (
                              <Tooltip
                                title={getTooltipContent(tooltips, TOOLTIP_CONFIG.setupHeader)?.text}
                                placement={getTooltipContent(tooltips, TOOLTIP_CONFIG.setupHeader)?.placement || "top"}
                              >
                                <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
                              </Tooltip>
                            )}
                          </div>
                          <div style={{ fontSize: '13px', fontWeight: 800, color: getScoreColor(r.setupScore, 30) }}>{r.setupScore}/30</div>
                        </div>
                        {pt.setup_block?.map((item, idx) => {
                          const itemTooltipKey = TOOLTIP_CONFIG.setupItems[idx];
                          const ttContent = getTooltipContent(tooltips, itemTooltipKey);
                          return (
                            <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '6px', padding: '3px 0', color: item.is_passed ? 'oklch(0.45 0.13 145)' : 'oklch(0.5 0.16 25)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Tooltip title={ttContent?.text} placement={ttContent?.placement || "top"}>
                                  <span>{item.is_passed ? '✓' : '✕'} {item.label}</span>
                                </Tooltip>
                              </div>
                              <span style={{ fontWeight: 600 }}>{item.score_text}</span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Risk Block */}
                      <div style={{ border: '1px solid oklch(0.92 0.005 90)', borderRadius: '12px', padding: '12px 14px', background: '#fff' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '12px', fontWeight: '700', color: 'oklch(0.4 0.01 90)', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                              Risk
                            </span>
                            {getTooltipContent(tooltips, TOOLTIP_CONFIG.riskHeader) && (
                              <Tooltip
                                title={getTooltipContent(tooltips, TOOLTIP_CONFIG.riskHeader)?.text}
                                placement={getTooltipContent(tooltips, TOOLTIP_CONFIG.riskHeader)?.placement || "top"}
                              >
                                <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
                              </Tooltip>
                            )}
                          </div>
                          <div style={{ fontSize: '13px', fontWeight: 800, color: getScoreColor(r.riskScore, 30) }}>{r.riskScore}/30</div>
                        </div>
                        {pt.risk_block && (() => {
                          const ttContent = getTooltipContent(tooltips, TOOLTIP_CONFIG.riskItems[0]);
                          return (
                            <div style={{ fontSize: '12.5px', lineHeight: '1.5', color: 'oklch(0.35 0.01 90)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Tooltip title={ttContent?.text} placement={ttContent?.placement}>
                                  <span>{pt.risk_block.has_sl ? '✅' : '❌'} {pt.risk_block.message}</span>
                                </Tooltip>
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Psychology Block */}
                      <div style={{ border: '1px solid oklch(0.92 0.005 90)', borderRadius: '12px', padding: '12px 14px', background: '#fff' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '12px', fontWeight: '700', color: 'oklch(0.4 0.01 90)', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                              Tâm lý
                            </span>
                            {getTooltipContent(tooltips, TOOLTIP_CONFIG.tamlyHeader) && (
                              <Tooltip
                                title={getTooltipContent(tooltips, TOOLTIP_CONFIG.tamlyHeader)?.text}
                                placement={getTooltipContent(tooltips, TOOLTIP_CONFIG.tamlyHeader)?.placement || "top"}
                              >
                                <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
                              </Tooltip>
                            )}
                          </div>
                          <div style={{ fontSize: '13px', fontWeight: 800, color: getScoreColor(r.psyScore, 40) }}>{r.psyScore}/40</div>
                        </div>
                        {pt.psychology_block?.map((item, idx) => {
                          const itemTooltipKey = TOOLTIP_CONFIG.tamlyItems[idx];
                          const ttContent = getTooltipContent(tooltips, itemTooltipKey);
                          return (
                            <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '6px', padding: '3px 0', color: item.is_passed ? 'oklch(0.45 0.13 145)' : 'oklch(0.5 0.16 25)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Tooltip title={ttContent?.text} placement={ttContent?.placement || "top"}>
                                  <span>{item.is_passed ? '✓' : '✕'} {item.label}</span>
                                </Tooltip>
                              </div>
                              <span style={{ fontWeight: 600 }}>{item.score_text}</span>  
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Timeline */}
                    <div style={{ border: '1px solid oklch(0.92 0.005 90)', borderRadius: '12px', padding: '12px 14px', background: '#fff' }}>
                      <div style={{ fontSize: '12px', fontWeight: 700, color: 'oklch(0.4 0.01 90)', textTransform: 'uppercase', letterSpacing: '0.02em', marginBottom: '10px' }}>
                        Lịch Sử Di Chuyển Lệnh (Timeline)
                      </div>
                      {r.timeline && r.timeline.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {r.timeline.map((item: any, idx) => {
                            const itemType = (item.type || item.event || '').toUpperCase();
                            const isOpen = itemType === 'OPEN';
                            const isClose = !isOpen && (isCloseEvent(item.data) || itemType === 'CLOSE');

                            const closeData = isClose ? (item.data as PositionTimeLineClose) : null;
                            const moveData = !isClose && !isOpen ? (item.data as PositionTimeLineMoveSL) : null;
                            const openData = isOpen ? item.data : null;

                            // Bóc tách giá mở lệnh từ openData hoặc postTradeData
                            const openPriceValue = openData?.open_price ?? openData?.price ?? pt.trade_details?.closed_sl;

                            const eventTime = item.time || item.data?.time || '';

                            const tone = (item.tone || item.data?.tone || '').toLowerCase();
                            const tag = (itemType || item.tag || item.data?.tag || '').toUpperCase();

                            let tagBg = 'oklch(94% 0.06 70)';
                            let tagColor = 'oklch(45% 0.14 55)';

                            if (tone === 'open' || tag.includes('OPEN')) {
                              tagBg = 'oklch(95% 0.05 220)';  // Nền xanh dương nhạt
                              tagColor = 'oklch(45% 0.16 220)'; // Chữ xanh dương
                            }
                            else if (tone === 'sl' || (tag.includes('SL') && !tag.includes('TP'))) {
                              tagBg = 'oklch(94% 0.04 25)';
                              tagColor = 'oklch(55% 0.18 25)';
                            }
                            else if (tone === 'tp' || (tag.includes('TP') && !tag.includes('SL'))) {
                              tagBg = 'oklch(96% 0.04 145)';
                              tagColor = 'oklch(50% 0.14 145)';
                            }
                            else if (tone === 'sltp' || (tag.includes('SL') && tag.includes('TP'))) {
                              tagBg = 'oklch(96% 0.06 85)';
                              tagColor = 'oklch(45% 0.14 85)';
                            }
                            else if (tone === 'close' || tag.includes('CLOSE')) {
                              tagBg = 'oklch(92% 0.02 80)';
                              tagColor = 'oklch(40% 0.02 80)';
                            }

                            let timelineTooltipKey: string | null = null;
                            if (tone === 'sl' || (tag.includes('SL') && !tag.includes('TP'))) {
                              timelineTooltipKey = TOOLTIP_CONFIG.timeline.move_sl;
                            } else if (tone === 'tp' || (tag.includes('TP') && !tag.includes('SL'))) {
                              timelineTooltipKey = TOOLTIP_CONFIG.timeline.move_tp;
                            } else if (tone === 'sltp' || (tag.includes('SL') && tag.includes('TP'))) {
                              timelineTooltipKey = TOOLTIP_CONFIG.timeline.move_both;
                            } else if (tone === 'close' || tag.includes('CLOSE') || isClose) {
                              timelineTooltipKey = TOOLTIP_CONFIG.timeline.close;
                            }
                            const timelineTt = timelineTooltipKey ? getTooltipContent(tooltips, timelineTooltipKey) : null;

                            return (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12.5px', padding: '8px 12px', background: 'oklch(0.975 0.005 90)', borderRadius: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  {timelineTt ? (
                                    <Tooltip title={timelineTt.text} placement={timelineTt.placement}>
                                      <span style={{
                                        padding: '2px 8px',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        background: tagBg,
                                        color: tagColor,
                                        cursor: 'pointer'
                                      }}>
                                        {itemType || 'LOG'}
                                      </span>
                                    </Tooltip>
                                  ) : (
                                    <span style={{
                                      padding: '2px 8px',
                                      borderRadius: '6px',
                                      fontSize: '11px',
                                      fontWeight: 700,
                                      textTransform: 'uppercase',
                                      background: tagBg,
                                      color: tagColor
                                    }}>
                                      {itemType || 'LOG'}
                                    </span>
                                  )}

                                  {/* Render Nội dung sự kiện OPEN kèm giá mở lệnh */}
                                  {isOpen ? (
                                    <span>
                                      <b>Mở vị thế</b>
                                      {openData?.side && openData.side !== 'UNKNOWN' && (
                                        <span style={{ marginLeft: '6px', fontWeight: 600 }}>[{openData.side}]</span>
                                      )}
                                      {openPriceValue && Number(openPriceValue) > 0 && (
                                        <span style={{ marginLeft: '6px', color: 'oklch(0.4 0.01 90)' }}>
                                          @ Giá mở: <b>{openPriceValue}</b>
                                        </span>
                                      )}
                                    </span>
                                  ) : isClose && closeData ? (
                                    /* Render Nội dung sự kiện CLOSE */
                                    <span>
                                      Đóng lệnh với lí do: <b>{closeData.reason}</b> | Giá đóng: <b>{closeData.close_price}</b> | PnL: <b style={{ color: Number(closeData.pnl) >= 0 ? 'oklch(0.45 0.13 145)' : 'oklch(0.5 0.16 25)' }}>{closeData.pnl}</b>
                                    </span>
                                  ) : moveData ? (
                                    /* Render Nội dung sự kiện MOVE SL/TP */
                                    <span>
                                      <b>{moveData.event}</b>
                                      <span style={{ marginLeft: '8px', color: 'oklch(0.4 0.01 90)' }}>
                                        SL: {moveData.old_sl || 0} → <b>{moveData.new_sl || 0}</b>
                                      </span>
                                      <span style={{ marginLeft: '8px', color: 'oklch(0.4 0.01 90)' }}>
                                        TP: {moveData.old_tp || 0} → <b>{moveData.new_tp || 0}</b>
                                      </span>
                                    </span>
                                  ) : (
                                    <span>{item.message || item.text || 'Cập nhật trạng thái lệnh'}</span>
                                  )}
                                </div>

                                {eventTime && (
                                  <div style={{ fontSize: '11.5px', color: 'oklch(0.55 0.01 90)' }}>
                                    {eventTime}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div style={{ fontSize: '12px', color: 'oklch(0.6 0.01 90)', fontStyle: 'italic' }}>
                          Chưa có lịch sử thay đổi vị thế lệnh.
                        </div>
                      )}
                    </div>

                    {/* Dynamic Mood Selector từ form_configs */}
                    <div style={panelStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: 'oklch(22% 0.01 80)' }}>Bạn đang cảm thấy gì?</div>
                        <div className="jd-seg" style={{ width: "160px" }}>
                          <button className={r.moodMode === "quick" ? "active" : ""} onClick={() => setMoodMode(r.id, "quick")}>Nhanh</button>
                          <button className={r.moodMode === "full" ? "active" : ""} onClick={() => setMoodMode(r.id, "full")}>Đầy đủ</button>
                        </div>
                      </div>

                      {r.moodMode === "quick" ? (
                        <div className="chip-group">
                          {quickEmotions.map((m) => {
                            const isSelected = (r.emotions || []).includes(m.value);
                            const toneClass = m.category === 'positive' ? 'pos' : m.category === 'dangerous' ? 'danger' : 'neg';
                            return (
                              <div
                                key={m.value}
                                className={`chip ${toneClass} ${isSelected ? "selected" : ""}`}
                                onClick={() => toggleEmotion(r.id, m.value)}
                              >
                                {m.label_vi || m.value}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="mood-full">
                          {emotionGroups.map((group, groupIdx) => (
                            <React.Fragment key={group.categoryKey}>
                              <div className="mood-group-label" style={{ marginTop: groupIdx === 0 ? 0 : '12px' }}>
                                <span className="dot" style={{ background: group.color }}></span> {group.title}
                              </div>
                              <div className="chip-group">
                                {group.items.map((m) => {
                                  const isSelected = (r.emotions || []).includes(m.value);
                                  const toneClass = group.categoryKey === 'positive' ? 'pos' : group.categoryKey === 'dangerous' ? 'danger' : 'neg';
                                  return (
                                    <div
                                      key={m.value}
                                      className={`chip ${toneClass} ${isSelected ? "selected" : ""}`}
                                      onClick={() => toggleEmotion(r.id, m.value)}
                                    >
                                      {m.label_vi || m.value}
                                    </div>
                                  );
                                })}
                              </div>
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    {
                      can_edit ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 20, paddingTop: 16, borderTop: `1px dashed ${C.border}` }}>
                          <span style={{ fontSize: 13, color: C.faint }}></span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {/* Reset */}
                            <button
                              onClick={() => resetTrade(r.id)}
                              disabled={saveStatus === 'saving'}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px',
                                border: `1px solid ${C.neutralBd}`, borderRadius: 10, background: C.surface,
                                color: C.muted, fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                                cursor: saveStatus === 'saving' ? 'not-allowed' : 'pointer',
                                opacity: saveStatus === 'saving' ? 0.6 : 1
                              }}
                            >
                              <RotateCcw size={15} /> {t('Reset')}
                            </button>

                            {/* Save */}
                            <button
                              onClick={() => saveTrade(r)}
                              disabled={saveStatus === 'saving' || saveStatus === 'saved'}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px',
                                border: 'none', borderRadius: 10, fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                                transition: 'all 0.3s ease',
                                cursor: (saveStatus === 'idle' || saveStatus === 'error') ? 'pointer' : 'not-allowed',
                                background: saveStatus === 'saving'
                                  ? C.neutralBd
                                  : saveStatus === 'saved'
                                    ? C.green
                                    : saveStatus === 'error'
                                      ? C.red
                                      : C.blue,
                                color: C.white,
                              }}
                            >
                              {saveStatus === 'saving' && (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" style={{ animation: 'jd-keyframes-spin 1s linear infinite' }}>
                                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                </svg>
                              )}
                              {saveStatus === 'saved' && (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M20 6L9 17l-5-5" />
                                </svg>
                              )}
                              {saveStatus === 'error' && (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <line x1="18" y1="6" x2="6" y2="18"></line>
                                  <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                              )}
                              {saveStatus === 'idle' && <Save size={15} />}

                              {saveStatus === 'saving' && t('Saving...')}
                              {saveStatus === 'saved' && t('Saved!')}
                              {saveStatus === 'error' && t('Error!')}
                              {saveStatus === 'idle' && t('Save')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        null
                      )
                    }
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', fontSize: '12.5px', color: 'oklch(0.55 0.01 90)' }}>
          <div>Hiển thị {processedTrades.length} / {totalCount} lệnh</div>
          <div>Điểm trung bình ngày: <b style={{ color: 'oklch(0.3 0.01 90)' }}>{avgScore}</b></div>
        </div>
      </div>
    </div>
  );
};

export default StepPostTradeV2;